/**
 * shared/collision/loader.ts — load a map's CollisionWorld from collision.bin / collision.json.
 *
 * OWNER: COLLISION agent. Same code in Node (server), the Worker (single player) and the browser
 * (prediction); the result is bit-identical in all of them (see world.ts header).
 *
 * Pipeline (format `bo2sns-collision-1`, tools/map/README.md):
 *  1. parse the sections (format.ts);
 *  2. pick the geometry that exists at runtime in Sticks and Stones:
 *     - world model (`model`/`triModel` == 0): all brushes, plus clipMap triangles (triSource 1) and
 *       static-model XModelCollSurf triangles (triSource 2). Brush faces (triSource 0) are NOT used as
 *       triangles: brushes are clipped as brushes (exact CM_TraceThroughBrush semantics);
 *     - entity submodels `*N` (spawns.json `triggers[]`), translated by the entity origin:
 *         glass                → included (CONTENTS.GLASS panes, surfaceType glass 9);
 *         script_brushmodel    → included only if the gametype keeps it. _gameobjects.gsc entity_is_allowed()
 *                                deletes every entity whose script_gameobjectname does not list the gametype,
 *                                and sas.gsc calls _gameobjects::main(["sas"]) — on Hijacked all 16 are
 *                                bombzone/hq clips, so none survive in SnS;
 *         trigger_* / heli_height_lock → excluded (triggers are not solid; kill volumes belong to the
 *                                gametype, sim/gametype/killvolumes.ts).
 *  3. convert three → CoD space once: cod = (x, -z, y) for points and normals; plane dist unchanged
 *     (README "Conventions"; a proper rotation, so CCW winding/outward normals are preserved);
 *  4. add the missing edge bevels (exact Minkowski sum, see BuildOptions.addBevels) and build the BVH
 *     (geometry.ts, bvh.ts, world.ts): ~0.15-0.25 s for Hijacked in Node/Chromium/WebKit, so no offline bake.
 */
import type { AssetSource } from '../assets';
import type { MapName } from '../protocol/control';
import type { Vec3 } from '../math';
import { CONTENTS, SURF, type CollisionWorld } from './types';

import { parseCollision, type CollisionJson, type RawCollision } from './format';
import { CollisionGeometryBuilder } from './geometry';
import { MeshCollisionWorld } from './world';

/** The gametype whose gameobjects survive _gameobjects::main (sas.gsc: allowed[0] = "sas"). */
const GAMETYPE = 'sas';

/** spawns.json `triggers[]` entry (subset). `origin` is three space. */
export interface MapEntityJson {
  classname: string;
  model: string;
  origin: number[];
  originCod?: number[];
  keys?: Record<string, string>;
}

export interface SpawnsEntitiesJson {
  triggers?: MapEntityJson[];
  [k: string]: unknown;
}

/** _gameobjects.gsc entity_is_allowed(): keep entities without script_gameobjectname, "[all_modes]", or listing the gametype. */
export function entityAllowedInGametype(e: MapEntityJson, gametype = GAMETYPE): boolean {
  const name = e.keys?.script_gameobjectname;
  if (name === undefined || name === '[all_modes]') return true;
  return name.split(' ').filter((s) => s.length > 0).includes(gametype);
}

/** Which submodel entities collide at runtime (see header). Returns model index -> three-space origin. */
export function solidSubmodels(spawns: SpawnsEntitiesJson | null, gametype = GAMETYPE): Map<number, [number, number, number]> {
  const out = new Map<number, [number, number, number]>();
  for (const e of spawns?.triggers ?? []) {
    if (!e.model || e.model[0] !== '*') continue;
    const n = Number(e.model.slice(1));
    if (!Number.isInteger(n) || n <= 0) continue;
    const solid = e.classname === 'glass' || ((e.classname === 'script_brushmodel' || e.classname === 'script_model') && entityAllowedInGametype(e, gametype));
    if (!solid) continue;
    out.set(n, [e.origin[0], e.origin[1], e.origin[2]]);
  }
  return out;
}

export interface BuildOptions {
  /** Submodels to include (model index -> three-space entity origin). Default: none. */
  submodels?: Map<number, [number, number, number]>;
  /**
   * Add the missing edge x axis bevels to map brushes (default true). The compiled sides already carry most
   * of them (6325 of 49628 exported sides on Hijacked touch their brush only along an edge); 901 more are
   * added so the AABB sweep is the exact Minkowski sum. Reason: T6 moves the player with a GJK sweep against
   * each brush's vertex hull (cbrush_t.verts, T6_Assets.h; T5 bg_slidemove.cpp gjk_brush_t), which is exact
   * convex collision with no "ghost" hits along the extension of sloped sides.
   */
  addBevels?: boolean;
  /**
   * Make static-model triangles that touch a ladder brush (surface flag LADDER, AABB grown by 4 u) non-solid
   * for players (default true; #B6 / #P1, ESTIMATE). false keeps the raw export.
   */
  ladderModelsNotPlayerSolid?: boolean;
  /** Static-model indices whose whole collision is a ladder xmodel (collision.json staticModels[].xmodel ~ /ladder/). */
  ladderStaticModels?: ReadonlySet<number>;
  /** Override the reported bounds. */
  bounds?: { mins: Vec3; maxs: Vec3 };
}

/** Build the CollisionWorld from parsed collision data (pure; used by the loader, tests and tools). */
export function buildCollisionWorld(name: string, raw: RawCollision, opts: BuildOptions = {}): MeshCollisionWorld {
  const subs = opts.submodels ?? new Map<number, [number, number, number]>();
  const g = new CollisionGeometryBuilder();
  const { brushes, planeF, planeU } = raw;
  const planes: number[] = [];
  const surf: number[] = [];
  for (let b = 0; b < raw.numBrushes; b++) {
    const contents = brushes[b * 5] >>> 0;
    const model = brushes[b * 5 + 1] | 0;
    let ox = 0, oy = 0, oz = 0;
    if (model !== 0) {
      const o = subs.get(model);
      if (!o) continue;
      ox = o[0]; oy = o[1]; oz = o[2];
    }
    const first = brushes[b * 5 + 2], count = brushes[b * 5 + 3];
    planes.length = 0; surf.length = 0;
    for (let i = first; i < first + count; i++) {
      // three space normal (a, b, c), inside n·p <= dist
      const a = planeF[i * 6], bb = planeF[i * 6 + 1], c = planeF[i * 6 + 2];
      let d = planeF[i * 6 + 3];
      if (model !== 0) d += a * ox + bb * oy + c * oz; // entity-local -> world
      planes.push(a, -c, bb, d); // cod = (x, -z, y)
      surf.push(planeU[i * 6 + 5]);
    }
    g.currentModel = model;
    g.addBrush({ planes, surfaceFlags: surf, contents }, b, opts.addBevels ?? true);
  }
  const P = raw.positions, I = raw.indices;
  const ladders = opts.ladderModelsNotPlayerSolid === false ? [] : ladderBoxes(raw);
  const ladderModels = opts.ladderModelsNotPlayerSolid === false ? null : (opts.ladderStaticModels ?? null);
  for (let t = 0; t < raw.numTris; t++) {
    const src = raw.triSource[t];
    if (src !== 1 && src !== 2) continue;
    const model = raw.triModel[t];
    let ox = 0, oy = 0, oz = 0;
    if (model !== 0) {
      const o = subs.get(model);
      if (!o) continue;
      ox = o[0]; oy = o[1]; oz = o[2];
    }
    let contents = raw.triContents[t] >>> 0;
    if (contents === 0) continue;
    const i0 = I[t * 3] * 3, i1 = I[t * 3 + 1] * 3, i2 = I[t * 3 + 2] * 3;
    if (src === 2 && model === 0 && ((ladderModels !== null && ladderModels.has(raw.triOwner[t])) || (ladders.length > 0 && touchesLadder(ladders, P, i0, i1, i2)))) {
      // (integration, CONTRACT-REQUESTS #B6 / #P1) ESTIMATE: ladder rung static models (clt_ladder_construction_01)
      // stand ~3 u in front of the SURF_LADDER playerclip, so PM_CheckLadderMove would hit a rung and never
      // engage; the Hijacked ladders are climbable in retail. Those triangles stop players but still stop
      // shots and missiles (MASK_SHOT / MASK_MISSILESHOT).
      contents = (contents & ~CONTENTS.SOLID) | CONTENTS.CANSHOOTCLIP | CONTENTS.MISSILECLIP;
    }
    g.currentModel = model;
    g.addTriangle(
      P[i0] + ox, -(P[i0 + 2] + oz), P[i0 + 1] + oy,
      P[i1] + ox, -(P[i1 + 2] + oz), P[i1 + 1] + oy,
      P[i2] + ox, -(P[i2 + 2] + oz), P[i2 + 1] + oy,
      contents, raw.triSurfaceFlags[t] >>> 0, src,
    );
  }
  return new MeshCollisionWorld(name, g, opts.bounds);
}

/** Three-space AABBs (min xyz, max xyz) of world brushes that carry the ladder surface flag, grown by 4 u. */
function ladderBoxes(raw: RawCollision): number[][] {
  const out: number[][] = [];
  const GROW = 4;
  for (let b = 0; b < raw.numBrushes; b++) {
    if ((raw.brushes[b * 5 + 1] | 0) !== 0) continue;
    const first = raw.brushes[b * 5 + 2], count = raw.brushes[b * 5 + 3];
    let ladder = false;
    for (let i = first; i < first + count; i++) if (raw.planeU[i * 6 + 5] & SURF.LADDER) { ladder = true; break; }
    if (!ladder) continue;
    const bb = raw.brushBounds;
    out.push([bb[b * 6] - GROW, bb[b * 6 + 1] - GROW, bb[b * 6 + 2] - GROW, bb[b * 6 + 3] + GROW, bb[b * 6 + 4] + GROW, bb[b * 6 + 5] + GROW]);
  }
  return out;
}

function touchesLadder(boxes: number[][], P: Float32Array, i0: number, i1: number, i2: number): boolean {
  for (const l of boxes) {
    let inside = true;
    for (let k = 0; k < 3 && inside; k++) {
      const lo = Math.min(P[i0 + k], P[i1 + k], P[i2 + k]), hi = Math.max(P[i0 + k], P[i1 + k], P[i2 + k]);
      if (hi < l[k] || lo > l[k + 3]) inside = false;
    }
    if (inside) return true;
  }
  return false;
}

/**
 * Player-relevant AABB per map (CoD space), used as `CollisionWorld.bounds` (POS_SCALE sanity, NET §6.2).
 * Hijacked, measured with `npx tsx tools/collision/inspect.ts mp_hijacked` (16-unit grid of standing-hull
 * drops, MASK_PLAYERSOLID_WORLD, walkable normal.z > 0.7, above the ocean): standable origins span
 * x -3524..2844, y -976..1008, z -302..309. Grown by the hull radius 15 and rounded out to 64; z is bounded by
 * the ocean kill volume (water surface -315, trigger_hurt below -311) and the playerclip ceiling brush at
 * z = 384 that covers the whole map. Relative to mapCenter (-60.5, -0.5, 1) that is within ±3524 x, so
 * POS_SCALE = 8 with int16 (±4096) holds. Maps not listed use the AABB of all player-solid geometry.
 */
const PLAYABLE_BOUNDS: Partial<Record<MapName, { mins: Vec3; maxs: Vec3 }>> = {
  mp_hijacked: { mins: [-3584, -1024, -384], maxs: [2880, 1024, 384] },
};

export async function loadCollisionWorld(assets: AssetSource, map: MapName): Promise<CollisionWorld> {
  const dir = `maps/${map}/`;
  const [json, bin, spawns] = await Promise.all([
    assets.json<CollisionJson>(dir + 'collision.json'),
    assets.binary(dir + 'collision.bin'),
    assets.json<SpawnsEntitiesJson>(dir + 'spawns.json').catch(() => null),
  ]);
  const raw = parseCollision(json, bin);
  return buildCollisionWorld(map, raw, { submodels: solidSubmodels(spawns), bounds: PLAYABLE_BOUNDS[map], ladderStaticModels: ladderStaticModels(json) });
}

/** #B6: indices of static models that are ladder xmodels (Hijacked: 4 x clt_ladder_construction_01). */
export function ladderStaticModels(json: CollisionJson): Set<number> {
  const out = new Set<number>();
  const list = json.staticModels;
  if (!Array.isArray(list)) return out;
  list.forEach((m, i) => { if (m && typeof m === 'object' && /ladder/i.test(String((m as { xmodel?: unknown }).xmodel ?? ''))) out.add(i); });
  return out;
}

